import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import CompanyLevelTag from "components/CompanyLevel/shared/components/CompanyLevelTag";
import Autocomplete from "components/DesignComponents/Autocomplete";
import AutocompleteListBox from "components/Filters/components/AutocompleteListBox";
import HighlightedOption from "components/HighlightedOption";
import ParamsContext from "context/params/paramsContext";
import { DROPDOWN_SIZES, FILTER_NAMES } from "shared/types/enums";
import { Filter } from "shared/types/interfaces";

import { CompanyFilterContext } from "../../CompanyFilterState";
import CompanyLevelTagLoader from "../../components/CompanyLevelTagLoader";
import { useRootCompanySearch } from "../../hooks";
import CustomPaper from "./CustomPaper";

const RootCompany = () => {
    const {
        filterDropdownOptions,
        rootCompanyLevel,
        rootCompany,
        changeRootCompany
    } = useContext(ParamsContext);

    const { isLoading } = useContext(CompanyFilterContext);

    const { t } = useTranslation();
    const { rootCompanyFilterOptions } = useRootCompanySearch();

    const [isLocalLoading, setIsLocalLoading] = useState(isLoading);
    const [inputValue, setInputValue] = useState("");

    const rootCompanies = filterDropdownOptions[FILTER_NAMES.RootCompany] ?? [];

    useEffect(() => {
        setIsLocalLoading(isLoading);
    }, [isLoading]);

    const handleChange = (companyId: string) => {
        changeRootCompany(companyId);
        setIsLocalLoading(true);
    };

    const renderLevelTag = () =>
        isLocalLoading ? (
            <CompanyLevelTagLoader />
        ) : (
            <CompanyLevelTag level={rootCompanyLevel} />
        );

    return (
        <div
            css={css({
                padding: "12px 16px 4px 16px"
            })}
        >
            <Autocomplete
                fullWidth
                size={DROPDOWN_SIZES.Medium}
                labelLeft={t("Dialog##root company")}
                includeInputInList
                disableClearable
                value={rootCompany}
                options={rootCompanies}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_, newInputValue) =>
                    setInputValue(newInputValue)
                }
                onChange={(_, newFilterValue) =>
                    handleChange(String(newFilterValue.id))
                }
                onClose={() => setInputValue("")}
                onKeyDown={e => e.stopPropagation()} // prevents from losing focus on pressing "c" key
                slots={{
                    paper: CustomPaper,
                    listbox: AutocompleteListBox
                }}
                getOptionLabel={(option: Filter) => option.name}
                iconRight={renderLevelTag()}
                filterOptions={(options, { inputValue }) =>
                    rootCompanyFilterOptions(options, inputValue.toLowerCase())
                }
                renderOption={(propsData, options) => {
                    return (
                        <li {...propsData} key={options.id}>
                            <div>
                                <HighlightedOption
                                    text={options.name}
                                    query={inputValue}
                                />
                            </div>

                            <div>{options.id}</div>
                        </li>
                    );
                }}
            />
        </div>
    );
};

export default RootCompany;
